import React, { useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Upload,
  Settings,
  Download,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Truck,
  Coins,
  ShoppingBag,
  Landmark,
  Search,
  Link as LinkIcon,
  Unlink,
  Check,
  FileSpreadsheet,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FolderSync,
} from 'lucide-react';
import {
  fetchDeliveredByYm,
  syncCarrierFolder,
  uploadCarrierManifest,
  fetchCarrierRates,
  updateCarrierRates,
  manualMatchCarrierRecord,
  manualUnmatchCarrierRecord,
  syncWooDeliveredStatuses,
  type CarrierRateConfig,
  type UnmatchedCarrierRecordItem,
  type MatchedOrderItem,
  type SyncWooStatusResult,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { formatTnd } from '../lib/format.js';
import { cn } from '../ui/cn.js';

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

function generateMonthOptions(): Array<{ ym: string; label: string }> {
  const options: Array<{ ym: string; label: string }> = [];
  const startYear = 2023;
  const startMonth = 3; // Starts strictly March 2023 per user request
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  for (let y = startYear; y <= currentYear; y++) {
    const mStart = y === startYear ? startMonth : 1;
    const mEnd = y === currentYear ? currentMonth : 12;
    for (let m = mStart; m <= mEnd; m++) {
      const ym = `${y}-${String(m).padStart(2, '0')}`;
      const label = `${MONTH_NAMES[m - 1]} ${y}`;
      options.push({ ym, label });
    }
  }
  return options;
}

function formatDateFr(isoDate: string | null | undefined): string {
  if (!isoDate) return '—';
  const clean = isoDate.split('T')[0];
  const parts = clean?.split('-');
  if (parts && parts.length === 3 && parts[0]?.length === 4) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return isoDate;
}

export function DeliveredByYmPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Month navigation: default to 2023-03 per user instruction
  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const [selectedYm, setSelectedYm] = useState<string>('2023-03');

  // Filters & Tabs
  const [selectedTab, setSelectedTab] = useState<
    'all' | 'matched' | 'amount_mismatch' | 'returned' | 'unmatched' | 'orphans'
  >('all');
  const [selectedCarrier, setSelectedCarrier] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [perPage] = useState<number>(50);
  const [breakdownOpen, setBreakdownOpen] = useState<boolean>(true);

  // Modals state
  const [ratesModalOpen, setRatesModalOpen] = useState<boolean>(false);
  const [ratesForm, setRatesForm] = useState<CarrierRateConfig[]>([]);
  const [syncWooModalOpen, setSyncWooModalOpen] = useState<boolean>(false);
  const [syncWooDryRun, setSyncWooDryRun] = useState<boolean>(true);
  const [syncWooResults, setSyncWooResults] = useState<SyncWooStatusResult | null>(null);

  // Manual match from orphan carrier parcel modal state
  const [manualMatchModalOpen, setManualMatchModalOpen] = useState<boolean>(false);
  const [selectedRecordForMatch, setSelectedRecordForMatch] = useState<UnmatchedCarrierRecordItem | null>(null);
  const [manualMatchSearch, setManualMatchSearch] = useState<string>('');

  // Manual match from unmatched Woo order modal state
  const [orderMatchModalOpen, setOrderMatchModalOpen] = useState<boolean>(false);
  const [selectedOrderForMatch, setSelectedOrderForMatch] = useState<MatchedOrderItem | null>(null);
  const [orderMatchSearch, setOrderMatchSearch] = useState<string>('');

  // Active status filter mapped to API status query
  const apiStatusParam = useMemo(() => {
    if (selectedTab === 'orphans') return undefined;
    if (selectedTab === 'all') return undefined;
    return selectedTab;
  }, [selectedTab]);

  // Query: Main delivered-by-ym reconciliation data
  const dataQuery = useQuery({
    queryKey: ['accounting', 'delivered-by-ym', selectedYm, apiStatusParam, selectedCarrier, searchQuery, page, perPage],
    queryFn: () =>
      fetchDeliveredByYm({
        ym: selectedYm,
        status: apiStatusParam,
        carrier: selectedCarrier !== 'all' ? selectedCarrier : undefined,
        search: searchQuery || undefined,
        page,
        perPage,
      }),
  });

  // Query: Carrier rates
  const ratesQuery = useQuery({
    queryKey: ['accounting', 'carrier-rates'],
    queryFn: fetchCarrierRates,
  });

  // Mutations
  const syncFolderMutation = useMutation({
    mutationFn: syncCarrierFolder,
    onSuccess: (res) => {
      const filesCount = res.filesScanned ?? res.totalFiles ?? 0;
      const rowsCount = res.totalRows ?? res.scannedFiles?.reduce((a, b) => a + b.totalRows, 0) ?? 0;
      alert(`Synchronisation terminée : ${filesCount} fichier(s) scanné(s), ${rowsCount} lignes traitées.`);
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
    },
    onError: (err: Error) => {
      alert(`Erreur de scan du dossier : ${err.message}`);
    },
  });

  const uploadManifestMutation = useMutation({
    mutationFn: uploadCarrierManifest,
    onSuccess: (res) => {
      const carrier = res.carrierName || 'Transporteur';
      const delivered = res.deliveredRows ?? res.deliveredCount ?? 0;
      const returned = res.returnedRows ?? res.returnedCount ?? 0;
      const total = res.totalRows ?? (delivered + returned);
      alert(
        `Manifeste "${res.filename}" importé avec succès pour ${carrier} : ${total} lignes (${delivered} livrés, ${returned} retours).`,
      );
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
    },
    onError: (err: Error) => {
      alert(`Échec de l'import : ${err.message}`);
    },
  });

  const saveRatesMutation = useMutation({
    mutationFn: updateCarrierRates,
    onSuccess: () => {
      alert('Tarifs transporteurs mis à jour avec succès.');
      setRatesModalOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
    },
    onError: (err: Error) => {
      alert(`Erreur lors de l'enregistrement des tarifs : ${err.message}`);
    },
  });

  const manualMatchMutation = useMutation({
    mutationFn: ({ carrierRecordId, wooOrderId }: { carrierRecordId: number; wooOrderId: number }) =>
      manualMatchCarrierRecord(carrierRecordId, wooOrderId),
    onSuccess: () => {
      alert('Rapprochement manuel enregistré avec succès.');
      setManualMatchModalOpen(false);
      setSelectedRecordForMatch(null);
      setOrderMatchModalOpen(false);
      setSelectedOrderForMatch(null);
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
    },
    onError: (err: Error) => {
      alert(`Échec du rapprochement : ${err.message}`);
    },
  });

  const manualUnmatchMutation = useMutation({
    mutationFn: (carrierRecordId: number) => manualUnmatchCarrierRecord(carrierRecordId),
    onSuccess: () => {
      alert('Liaison supprimée avec succès.');
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
    },
    onError: (err: Error) => {
      alert(`Erreur lors de la suppression du lien : ${err.message}`);
    },
  });

  const syncWooStatusesMutation = useMutation({
    mutationFn: syncWooDeliveredStatuses,
    onSuccess: (res) => {
      setSyncWooResults(res);
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
    },
    onError: (err: Error) => {
      alert(`Erreur de synchronisation Woo : ${err.message}`);
    },
  });

  // Current month navigation indexes
  const currentMonthIndex = monthOptions.findIndex((m) => m.ym === selectedYm);
  const hasPrevMonth = currentMonthIndex > 0;
  const hasNextMonth = currentMonthIndex < monthOptions.length - 1;

  const handlePrevMonth = () => {
    if (hasPrevMonth) {
      setSelectedYm(monthOptions[currentMonthIndex - 1]?.ym ?? '2023-03');
      setPage(1);
    }
  };

  const handleNextMonth = () => {
    if (hasNextMonth) {
      setSelectedYm(monthOptions[currentMonthIndex + 1]?.ym ?? '2023-03');
      setPage(1);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadManifestMutation.mutate(file);
      e.target.value = '';
    }
  };

  const handleOpenRatesModal = () => {
    if (ratesQuery.data?.rates) {
      setRatesForm(JSON.parse(JSON.stringify(ratesQuery.data.rates)));
    }
    setRatesModalOpen(true);
  };

  const handleOpenManualMatch = (record: UnmatchedCarrierRecordItem) => {
    setSelectedRecordForMatch(record);
    setManualMatchSearch(record.orderRef || record.customerPhone?.slice(-8) || '');
    setManualMatchModalOpen(true);
  };

  const handleExportXlsx = () => {
    window.open(`/api/v1/accounting/delivered-by-ym/export.xlsx?ym=${selectedYm}`, '_blank');
  };

  const kpis = dataQuery.data?.kpis;
  const carrierBreakdown = dataQuery.data?.carrierBreakdown ?? [];
  const orders = dataQuery.data?.orders ?? [];
  const unmatchedCarriers = dataQuery.data?.unmatchedCarrierRecords ?? [];
  const pagination = dataQuery.data?.pagination;

  // Filter candidate woo orders for the manual matching modal
  const manualMatchCandidateOrders = useMemo(() => {
    if (!orders || !manualMatchSearch.trim()) return orders.slice(0, 10);
    const q = manualMatchSearch.toLowerCase().trim();
    return orders.filter(
      (m) =>
        m.order.number.toLowerCase().includes(q) ||
        (m.order.customerName && m.order.customerName.toLowerCase().includes(q)) ||
        (m.order.billingPhone && m.order.billingPhone.includes(q)) ||
        (m.order.phoneDigits && m.order.phoneDigits.includes(q)),
    );
  }, [orders, manualMatchSearch]);

  const handleOpenOrderMatch = (item: MatchedOrderItem) => {
    setSelectedOrderForMatch(item);
    setOrderMatchSearch(item.order.phoneDigits?.slice(-8) || item.order.number || '');
    setOrderMatchModalOpen(true);
  };

  // Filter candidate orphan carrier records for the order matching modal
  const candidateCarriersForOrder = useMemo(() => {
    if (!selectedOrderForMatch || !unmatchedCarriers.length) return [];
    const q = orderMatchSearch.trim().toLowerCase();
    if (!q) {
      const orderPhone = selectedOrderForMatch.order.phoneDigits?.slice(-8);
      const orderNum = selectedOrderForMatch.order.number.toLowerCase();
      const priority = unmatchedCarriers.filter(
        (c) =>
          (orderPhone && c.customerPhone?.includes(orderPhone)) ||
          (c.orderRef && c.orderRef.toLowerCase().includes(orderNum)) ||
          (selectedOrderForMatch.order.customerName &&
            c.customerName &&
            c.customerName.toLowerCase().includes(selectedOrderForMatch.order.customerName.toLowerCase().slice(0, 4))),
      );
      const rest = unmatchedCarriers.filter((c) => !priority.includes(c));
      return [...priority, ...rest];
    }
    return unmatchedCarriers.filter(
      (c) =>
        c.trackingNumber.toLowerCase().includes(q) ||
        (c.customerName && c.customerName.toLowerCase().includes(q)) ||
        (c.customerPhone && c.customerPhone.includes(q)) ||
        (c.orderRef && c.orderRef.toLowerCase().includes(q)) ||
        (c.destination && c.destination.toLowerCase().includes(q)) ||
        String(c.amount).includes(q),
    );
  }, [selectedOrderForMatch, unmatchedCarriers, orderMatchSearch]);

  return (
    <div className="space-y-6 pb-12">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".xlsx,.xls,.csv"
        className="hidden"
      />

      {/* Top Page Header */}
      <PageHeader
        index="S25"
        eyebrow="COMPTABILITÉ & RAPPROCHEMENT"
        title="Rapprochement Comptable — Livrées par Année & Mois"
        purpose="Contrôle mensuel des commandes WooCommerce face aux bordereaux de livraison transporteurs (Aramex, Navex, Mylerz, Shiparon). Calcul automatique du COD collecté, des frais et du net reversé."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncFolderMutation.mutate()}
              disabled={syncFolderMutation.isPending}
              className="gap-1.5 text-xs font-semibold"
            >
              <FolderSync className={cn('h-3.5 w-3.5', syncFolderMutation.isPending && 'animate-spin')} />
              <span>Scanner accounting/carrier</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadManifestMutation.isPending}
              className="gap-1.5 text-xs font-semibold"
            >
              <Upload className={cn('h-3.5 w-3.5', uploadManifestMutation.isPending && 'animate-spin')} />
              <span>Importer un fichier</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenRatesModal}
              className="gap-1.5 text-xs font-semibold"
            >
              <Settings className="h-3.5 w-3.5" />
              <span>Tarifs transporteurs</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSyncWooResults(null);
                setSyncWooDryRun(true);
                setSyncWooModalOpen(true);
              }}
              className={cn(
                'gap-1.5 text-xs font-semibold',
                (kpis?.syncWooEligibleCount ?? 0) > 0
                  ? 'text-emerald-800 border-emerald-400 bg-emerald-50/70 hover:bg-emerald-100 shadow-2xs font-bold'
                  : 'text-ink-soft border-border hover:bg-cream/40',
              )}
            >
              <RefreshCw
                className={cn(
                  'h-3.5 w-3.5',
                  (kpis?.syncWooEligibleCount ?? 0) > 0 ? 'text-emerald-600' : 'text-ink-ghost',
                )}
              />
              <span>Synchroniser statuts Woo ({kpis?.syncWooEligibleCount ?? 0})</span>
            </Button>

            <Button
              variant="soft"
              size="sm"
              onClick={handleExportXlsx}
              className="gap-1.5 text-xs font-semibold"
            >
              <Download className="h-3.5 w-3.5" />
              <span>Export Excel</span>
            </Button>
          </div>
        }
      />

      {/* Ribbon: Year & Month Selector */}
      <Panel className="p-4 bg-gradient-to-r from-card via-card to-indigo-50/30 border border-border shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-100/70 text-indigo-700 rounded-xl shadow-2xs border border-indigo-200">
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wider font-bold text-ink-ghost">Période d'analyse</span>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200">
                  Début historique : Mars 2023
                </span>
              </div>
              <h2 className="text-lg font-bold text-ink flex items-center gap-2">
                {monthOptions.find((m) => m.ym === selectedYm)?.label ?? selectedYm}
                <span className="text-xs font-mono font-normal text-ink-ghost">({selectedYm})</span>
              </h2>
            </div>
          </div>

          {/* Month Stepper & Dropdown */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevMonth}
              disabled={!hasPrevMonth || dataQuery.isFetching}
              className="gap-1 h-9 px-3"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline text-xs">Mois précédent</span>
            </Button>

            <select
              value={selectedYm}
              onChange={(e) => {
                setSelectedYm(e.target.value);
                setPage(1);
              }}
              className="h-9 px-3 bg-card border border-border rounded-lg text-xs font-bold text-ink shadow-2xs focus:ring-2 focus:ring-indigo-500 cursor-pointer"
            >
              {monthOptions.map((opt) => (
                <option key={opt.ym} value={opt.ym}>
                  {opt.label}
                </option>
              ))}
            </select>

            <Button
              variant="outline"
              size="sm"
              onClick={handleNextMonth}
              disabled={!hasNextMonth || dataQuery.isFetching}
              className="gap-1 h-9 px-3"
            >
              <span className="hidden sm:inline text-xs">Mois suivant</span>
              <ChevronRight className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => void dataQuery.refetch()}
              disabled={dataQuery.isFetching}
              className="h-9 px-2.5 text-ink-soft hover:text-ink"
              title="Rafraîchir les données du mois"
            >
              <RefreshCw className={cn('h-4 w-4', dataQuery.isFetching && 'animate-spin')} />
            </Button>
          </div>
        </div>
      </Panel>

      {/* 6 Financial & Delivery KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3.5">
        {/* KPI 1: CA WooCommerce */}
        <Panel className="p-3.5 border-l-4 border-l-sky-500 flex flex-col justify-between">
          <div className="flex items-center justify-between text-ink-ghost mb-1">
            <span className="text-[11px] font-bold uppercase tracking-wider">CA WooCommerce</span>
            <ShoppingBag className="h-4 w-4 text-sky-500" />
          </div>
          <div>
            <div className="text-xl font-black text-ink">{formatTnd(kpis?.totalWooAmount)}</div>
            <div className="text-[11px] text-ink-faint mt-0.5">
              {kpis?.totalOrders ?? 0} commande(s) Woo créée(s)
            </div>
          </div>
        </Panel>

        {/* KPI 2: Total Collecté (COD) */}
        <Panel className="p-3.5 border-l-4 border-l-emerald-600 flex flex-col justify-between bg-emerald-50/20">
          <div className="flex items-center justify-between text-ink-ghost mb-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">Total COD Collecté</span>
            <Coins className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <div className="text-xl font-black text-emerald-950">{formatTnd(kpis?.totalCollectedCod)}</div>
            <div className="text-[11px] text-emerald-700 font-semibold mt-0.5">
              {kpis?.totalDeliveredOrders ?? 0} colis livré(s) confirmés
            </div>
          </div>
        </Panel>

        {/* KPI 3: Frais Transporteurs HT */}
        <Panel className="p-3.5 border-l-4 border-l-amber-500 flex flex-col justify-between">
          <div className="flex items-center justify-between text-ink-ghost mb-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-amber-800">Frais de Livraison</span>
            <Truck className="h-4 w-4 text-amber-500" />
          </div>
          <div>
            <div className="text-xl font-black text-amber-950">{formatTnd(kpis?.totalFeesTotalHt)}</div>
            <div className="text-[10px] text-ink-faint mt-0.5 truncate" title={`TTC: ${formatTnd(kpis?.totalFeesTotalTtc)}`}>
              TTC: {formatTnd(kpis?.totalFeesTotalTtc)} (Liv: {formatTnd(kpis?.totalFeesDeliveryHt)} / Ret: {formatTnd(kpis?.totalFeesReturnHt)})
            </div>
          </div>
        </Panel>

        {/* KPI 4: Net Attendu */}
        <Panel className="p-3.5 border-l-4 border-l-indigo-600 flex flex-col justify-between bg-indigo-50/30">
          <div className="flex items-center justify-between text-ink-ghost mb-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-800">Net Reversé Attendu</span>
            <Landmark className="h-4 w-4 text-indigo-600" />
          </div>
          <div>
            <div className="text-xl font-black text-indigo-950">{formatTnd(kpis?.netTotalExpected)}</div>
            <div className="text-[11px] text-indigo-700 font-semibold mt-0.5">
              COD Net après déduction frais HT
            </div>
          </div>
        </Panel>

        {/* KPI 5: Taux de Couverture */}
        <Panel className="p-3.5 border-l-4 border-l-herb-600 flex flex-col justify-between">
          <div className="flex items-center justify-between text-ink-ghost mb-1">
            <span className="text-[11px] font-bold uppercase tracking-wider">Taux Couverture</span>
            <CheckCircle2 className="h-4 w-4 text-herb-600" />
          </div>
          <div>
            <div className="text-xl font-black text-ink">{kpis?.coverageRate ?? 0} %</div>
            <div className="w-full bg-cream rounded-full h-1.5 mt-1.5 overflow-hidden">
              <div
                className="bg-herb-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(100, kpis?.coverageRate ?? 0)}%` }}
              />
            </div>
          </div>
        </Panel>

        {/* KPI 6: Écarts & Retours */}
        <Panel className="p-3.5 border-l-4 border-l-rose-500 flex flex-col justify-between">
          <div className="flex items-center justify-between text-ink-ghost mb-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-rose-800">Écarts & Anomalies</span>
            <AlertTriangle className="h-4 w-4 text-rose-500" />
          </div>
          <div>
            <div className="text-xl font-black text-rose-950">
              {kpis?.amountMismatchCount ?? 0}{' '}
              <span className="text-xs font-normal text-rose-700 font-mono">
                ({formatTnd(kpis?.amountMismatchDelta)})
              </span>
            </div>
            <div className="text-[11px] text-ink-faint mt-0.5">
              {kpis?.returnedCount ?? 0} retours · {kpis?.unmatchedCount ?? 0} non rapprochées
            </div>
          </div>
        </Panel>
      </div>

      {/* Collapsible Carrier Breakdown Section */}
      <Panel className="border border-border overflow-hidden">
        <button
          type="button"
          onClick={() => setBreakdownOpen((prev) => !prev)}
          className="w-full flex items-center justify-between p-3.5 bg-cream/30 hover:bg-cream/60 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <Truck className="h-4 w-4 text-indigo-700" />
            <span className="text-xs font-bold uppercase tracking-wider text-ink">
              Répartition & Grille Financière par Transporteur ({selectedYm})
            </span>
            <span className="text-[11px] text-ink-ghost font-normal">
              ({carrierBreakdown.length} transporteur(s) actif(s))
            </span>
          </div>
          <div className="flex items-center gap-1 text-ink-soft text-xs">
            <span>{breakdownOpen ? 'Masquer' : 'Afficher'}</span>
            {breakdownOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </button>

        {breakdownOpen && (
          <div className="p-3 border-t border-border">
            {carrierBreakdown.length === 0 ? (
              <div className="text-center py-6 text-xs text-ink-faint">
                Aucun colis transporteur rapproché pour ce mois. Scannez le dossier ou importez un bordereau.
              </div>
            ) : (
              <TableShell>
                <Table>
                  <THead>
                    <Tr>
                      <Th>Transporteur</Th>
                      <Th className="text-center">Colis Livrés</Th>
                      <Th className="text-center">Colis Retours</Th>
                      <Th className="text-right">Total COD Collecté</Th>
                      <Th className="text-center">Tarif Livré HT</Th>
                      <Th className="text-center">Tarif Retour HT</Th>
                      <Th className="text-right">Frais Totaux HT</Th>
                      <Th className="text-right">Frais TTC</Th>
                      <Th className="text-right font-bold text-indigo-900">Net Reversé</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {carrierBreakdown.map((row) => (
                      <Tr key={row.carrierKey} className="hover:bg-cream/20">
                        <Td className="font-bold text-ink flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-indigo-600" />
                          {row.carrierLabel}
                        </Td>
                        <Td className="text-center font-bold text-emerald-800 bg-emerald-50/50">
                          {row.deliveredCount}
                        </Td>
                        <Td className="text-center font-semibold text-rose-800 bg-rose-50/50">
                          {row.returnedCount}
                        </Td>
                        <Td className="text-right font-mono font-bold text-emerald-900">
                          {formatTnd(row.collectedCod)}
                        </Td>
                        <Td className="text-center font-mono text-xs text-ink-faint">
                          {formatTnd(row.deliveryPriceHt)}
                        </Td>
                        <Td className="text-center font-mono text-xs text-ink-faint">
                          {formatTnd(row.returnPriceHt)}
                        </Td>
                        <Td className="text-right font-mono text-amber-900">
                          {formatTnd(row.totalFeesHt)}
                        </Td>
                        <Td className="text-right font-mono text-xs text-ink-ghost">
                          {formatTnd(row.totalFeesTtc)}
                        </Td>
                        <Td className="text-right font-mono font-black text-indigo-900 bg-indigo-50/30">
                          {formatTnd(row.netPayout)}
                        </Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </TableShell>
            )}
          </div>
        )}
      </Panel>

      {/* Tabs & Filters Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-border pb-3">
        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => {
              setSelectedTab('all');
              setPage(1);
            }}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-bold transition-all',
              selectedTab === 'all'
                ? 'bg-indigo-600 text-white shadow-2xs'
                : 'text-ink-soft hover:bg-cream/70 hover:text-ink',
            )}
          >
            Toutes ({kpis?.totalOrders ?? 0})
          </button>

          <button
            type="button"
            onClick={() => {
              setSelectedTab('matched');
              setPage(1);
            }}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5',
              selectedTab === 'matched'
                ? 'bg-emerald-600 text-white shadow-2xs'
                : 'text-emerald-800 bg-emerald-50/70 hover:bg-emerald-100',
            )}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Livrées ({kpis?.totalDeliveredOrders ?? 0})
          </button>

          <button
            type="button"
            onClick={() => {
              setSelectedTab('amount_mismatch');
              setPage(1);
            }}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5',
              selectedTab === 'amount_mismatch'
                ? 'bg-amber-600 text-white shadow-2xs'
                : 'text-amber-800 bg-amber-50/70 hover:bg-amber-100',
            )}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Écarts Montant ({kpis?.amountMismatchCount ?? 0})
          </button>

          <button
            type="button"
            onClick={() => {
              setSelectedTab('returned');
              setPage(1);
            }}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5',
              selectedTab === 'returned'
                ? 'bg-purple-600 text-white shadow-2xs'
                : 'text-purple-800 bg-purple-50/70 hover:bg-purple-100',
            )}
          >
            <XCircle className="h-3.5 w-3.5" />
            Retours ({kpis?.returnedCount ?? 0})
          </button>

          <button
            type="button"
            onClick={() => {
              setSelectedTab('unmatched');
              setPage(1);
            }}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5',
              selectedTab === 'unmatched'
                ? 'bg-rose-600 text-white shadow-2xs'
                : 'text-rose-800 bg-rose-50/70 hover:bg-rose-100',
            )}
          >
            <AlertCircle className="h-3.5 w-3.5" />
            Non rapprochées ({kpis?.unmatchedCount ?? 0})
          </button>

          <button
            type="button"
            onClick={() => {
              setSelectedTab('orphans');
              setPage(1);
            }}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5',
              selectedTab === 'orphans'
                ? 'bg-sky-700 text-white shadow-2xs'
                : 'text-sky-800 bg-sky-50/70 hover:bg-sky-100',
            )}
          >
            <LinkIcon className="h-3.5 w-3.5" />
            Colis orphelins transporteurs ({unmatchedCarriers.length})
          </button>
        </div>

        {/* Search & Carrier Filter */}
        <div className="flex items-center gap-2">
          {selectedTab !== 'orphans' && (
            <select
              value={selectedCarrier}
              onChange={(e) => {
                setSelectedCarrier(e.target.value);
                setPage(1);
              }}
              className="h-8 px-2.5 bg-card border border-border rounded-lg text-xs font-medium text-ink shadow-2xs"
            >
              <option value="all">Tous transporteurs</option>
              <option value="aramex">Aramex</option>
              <option value="navex">Navex</option>
              <option value="mylerz">Mylerz</option>
              <option value="jetpack">Jetpack (Shiparon)</option>
              <option value="freelance">Freelance</option>
            </select>
          )}

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-ghost" />
            <input
              type="text"
              placeholder="Commande, tél, AWB, client…"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="h-8 pl-8 pr-3 w-52 sm:w-64 bg-card border border-border rounded-lg text-xs text-ink placeholder:text-ink-ghost focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Main Table: Either Matched Woo Orders or Orphan Carrier Records */}
      {selectedTab === 'orphans' ? (
        /* Orphan Carrier Records Table */
        <Panel className="border border-border overflow-hidden">
          <div className="p-3 bg-sky-50/40 border-b border-border flex items-center justify-between">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-sky-950 flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-sky-700" />
                Lignes transporteurs non associées automatiquement ({unmatchedCarriers.length})
              </h3>
              <p className="text-[11px] text-sky-800">
                Ces colis figurent dans vos bordereaux transporteurs mais n'ont pas encore été rattachés à une commande WooCommerce.
                Cliquez sur « Lier » pour faire le rapprochement manuel instantané.
              </p>
            </div>
          </div>

          {unmatchedCarriers.length === 0 ? (
            <div className="text-center py-12 text-xs text-ink-faint">
              Aucun colis orphelin pour ce mois ! Toutes les lignes transporteurs sont rapprochées.
            </div>
          ) : (
            <TableShell>
              <Table>
                <THead>
                  <Tr>
                    <Th>Transporteur</Th>
                    <Th>AWB / Code Suivi</Th>
                    <Th>Réf Commande</Th>
                    <Th>Client / Tél</Th>
                    <Th>Gouvernorat</Th>
                    <Th className="text-right">Montant COD</Th>
                    <Th className="text-center">Statut Transporteur</Th>
                    <Th className="text-center">Date Livraison</Th>
                    <Th>Fichier Source</Th>
                    <Th className="text-right">Action</Th>
                  </Tr>
                </THead>
                <TBody>
                  {unmatchedCarriers.map((record) => (
                    <Tr key={record.id} className="hover:bg-cream/20">
                      <Td className="font-bold text-xs text-ink">
                        <span className="px-2 py-0.5 rounded bg-card border border-border text-[11px]">
                          {record.carrierName}
                        </span>
                      </Td>
                      <Td className="font-mono text-xs font-semibold text-indigo-700 select-all">
                        {record.trackingNumber}
                      </Td>
                      <Td className="font-mono text-xs text-ink-soft">
                        {record.orderRef || '—'}
                      </Td>
                      <Td className="text-xs">
                        <div className="font-semibold text-ink">{record.customerName || 'Client inconnu'}</div>
                        <div className="font-mono text-[11px] text-ink-ghost">{record.customerPhone || '—'}</div>
                      </Td>
                      <Td className="text-xs text-ink-soft">{record.destination || '—'}</Td>
                      <Td className="text-right font-mono font-bold text-ink">
                        {formatTnd(record.amount)}
                      </Td>
                      <Td className="text-center">
                        <span
                          className={cn(
                            'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase',
                            record.status === 'delivered'
                              ? 'bg-emerald-100 text-emerald-800'
                              : record.status === 'returned'
                                ? 'bg-rose-100 text-rose-800'
                                : 'bg-cream text-ink-faint',
                          )}
                        >
                          {record.statusRaw || record.status}
                        </span>
                      </Td>
                      <Td className="text-center text-xs text-ink-ghost font-mono">
                        {formatDateFr(record.deliveryDate)}
                      </Td>
                      <Td className="text-[11px] text-ink-ghost truncate max-w-[150px]">
                        <span title={record.filename ?? ''}>
                          {record.filename} {record.sheetName ? `(${record.sheetName})` : ''}
                        </span>
                      </Td>
                      <Td className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenManualMatch(record)}
                          className="text-xs font-bold text-indigo-700 border-indigo-300 hover:bg-indigo-50 h-7 px-2.5"
                        >
                          <LinkIcon className="h-3 w-3 mr-1" />
                          Lier à Woo
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </TableShell>
          )}
        </Panel>
      ) : (
        /* Orders Reconciliation Table */
        <Panel className="border border-border overflow-hidden">
          {dataQuery.isLoading ? (
            <div className="text-center py-16 text-xs text-ink-ghost flex flex-col items-center gap-2">
              <RefreshCw className="h-6 w-6 animate-spin text-indigo-600" />
              <span>Chargement du rapprochement pour {selectedYm}…</span>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16 text-xs text-ink-faint">
              Aucune commande trouvée pour ces critères en {selectedYm}.
            </div>
          ) : (
            <>
              <TableShell>
                <Table>
                  <THead>
                    <Tr>
                      <Th>Commande Woo</Th>
                      <Th>Statut Woo</Th>
                      <Th>Preuve Transporteur</Th>
                      <Th className="text-right">Montant Woo</Th>
                      <Th className="text-right">Montant COD</Th>
                      <Th className="text-center">Écart</Th>
                      <Th className="text-center">Résultat Matching</Th>
                      <Th className="text-center">Action Woo</Th>
                      <Th className="text-right">Action Manuel</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {orders.map((item) => {
                      const isMismatch = item.matchStatus === 'amount_mismatch';
                      const isReturned = item.matchStatus === 'returned';

                      return (
                        <Tr
                          key={item.order.id}
                          className={cn(
                            'hover:bg-cream/20',
                            isMismatch && 'bg-amber-50/20',
                            isReturned && 'bg-purple-50/15',
                          )}
                        >
                          {/* Commande Woo */}
                          <Td className="text-xs">
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono font-bold text-indigo-950">#{item.order.number}</span>
                              <span className="text-[10px] text-ink-ghost">
                                {formatDateFr(item.order.dateCreated)}
                              </span>
                            </div>
                            <div className="font-semibold text-ink truncate max-w-[180px]">
                              {item.order.customerName || 'Client'}
                            </div>
                            <div className="font-mono text-[11px] text-ink-ghost">
                              {item.order.billingPhone || '—'}
                            </div>
                          </Td>

                          {/* Statut Woo actuel */}
                          <Td className="text-xs">
                            <span
                              className={cn(
                                'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase',
                                item.order.status === 'completed'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : item.order.status === 'cancelled'
                                    ? 'bg-rose-100 text-rose-800'
                                    : item.order.status === 'processing'
                                      ? 'bg-sky-100 text-sky-800'
                                      : 'bg-cream text-ink-faint',
                              )}
                            >
                              {item.order.status}
                            </span>
                          </Td>

                          {/* Preuve Transporteur */}
                          <Td className="text-xs">
                            {item.carrierName ? (
                              <div>
                                <div className="font-bold text-ink flex items-center gap-1">
                                  <span>{item.carrierName}</span>
                                  {item.deliveryDate && (
                                    <span className="text-[10px] text-ink-ghost font-normal">
                                      ({formatDateFr(item.deliveryDate)})
                                    </span>
                                  )}
                                </div>
                                <div className="font-mono text-[11px] text-indigo-700 select-all font-semibold">
                                  {item.trackingNumber || '—'}
                                </div>
                                <div className="text-[10px] text-ink-faint font-medium">
                                  Preuve : {item.sourceProof}
                                </div>
                              </div>
                            ) : (
                              <span className="text-ink-ghost italic text-xs">Aucune preuve</span>
                            )}
                          </Td>

                          {/* Montant Woo */}
                          <Td className="text-right font-mono font-bold text-xs text-ink">
                            {formatTnd(item.order.total)}
                          </Td>

                          {/* Montant COD */}
                          <Td className="text-right font-mono font-bold text-xs">
                            {item.carrierAmount !== null ? (
                              <span className={cn(isMismatch ? 'text-amber-800 font-black' : 'text-emerald-900')}>
                                {formatTnd(item.carrierAmount)}
                              </span>
                            ) : (
                              <span className="text-ink-ghost">—</span>
                            )}
                          </Td>

                          {/* Écart */}
                          <Td className="text-center font-mono text-xs">
                            {item.amountDifference !== 0 ? (
                              <span
                                className={cn(
                                  'px-1.5 py-0.5 rounded text-[10px] font-bold font-mono',
                                  item.amountDifference > 0
                                    ? 'bg-amber-100 text-amber-900'
                                    : 'bg-rose-100 text-rose-900',
                                )}
                              >
                                {item.amountDifference > 0 ? `+${formatTnd(item.amountDifference)}` : formatTnd(item.amountDifference)}
                              </span>
                            ) : (
                              <span className="text-emerald-700 text-[11px]">0.000</span>
                            )}
                          </Td>

                          {/* Résultat Matching */}
                          <Td className="text-center text-xs">
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase',
                                item.matchStatus === 'matched'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : item.matchStatus === 'amount_mismatch'
                                    ? 'bg-amber-100 text-amber-900'
                                    : item.matchStatus === 'returned'
                                      ? 'bg-purple-100 text-purple-800'
                                      : 'bg-rose-50 text-rose-800 border border-rose-200',
                              )}
                              title={item.matchReason}
                            >
                              {item.matchStatus === 'matched' && <Check className="h-3 w-3" />}
                              {item.matchStatus === 'amount_mismatch' && <AlertTriangle className="h-3 w-3" />}
                              {item.matchStatus === 'returned' && <XCircle className="h-3 w-3" />}
                              {item.matchStatus === 'matched'
                                ? 'Livré Conforme'
                                : item.matchStatus === 'amount_mismatch'
                                  ? 'Écart COD'
                                  : item.matchStatus === 'returned'
                                    ? 'Retourné'
                                    : 'Non Rapproché'}
                            </span>
                            {item.matchReason && item.matchStatus !== 'unmatched' && (
                              <span
                                className="block text-[9px] text-ink-faint mt-1 truncate max-w-[11rem] mx-auto font-medium"
                                title={item.matchReason}
                              >
                                {item.matchReason}
                              </span>
                            )}
                          </Td>

                          {/* Action Woo */}
                          <Td className="text-center text-xs">
                            {item.syncWooEligible ? (
                              <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                Prêt à passer à {item.syncTargetStatus === 'completed' ? 'Terminée' : 'Annulée'}
                              </span>
                            ) : (
                              <span className="text-ink-ghost text-[10px]">Aligné</span>
                            )}
                          </Td>

                          {/* Action Manuel */}
                          <Td className="text-right text-xs">
                            {item.matchStatus === 'unmatched' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenOrderMatch(item)}
                                className="h-7 px-2.5 text-xs font-bold text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                                title="Rechercher et associer un colis transporteur"
                              >
                                <LinkIcon className="h-3 w-3 mr-1" />
                                Associer à un colis
                              </Button>
                            ) : item.carrierRecordId ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => manualUnmatchMutation.mutate(item.carrierRecordId!)}
                                className="h-7 px-2 text-rose-700 hover:bg-rose-50 hover:text-rose-900 text-[11px]"
                                title="Dissocier cette liaison"
                              >
                                <Unlink className="h-3 w-3 mr-1" />
                                Délier
                              </Button>
                            ) : null}
                          </Td>
                        </Tr>
                      );
                    })}
                  </TBody>
                </Table>
              </TableShell>

              {/* Pagination controls */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between p-3.5 border-t border-border bg-card">
                  <div className="text-xs text-ink-ghost">
                    Affichage de {(page - 1) * perPage + 1} à {Math.min(page * perPage, pagination.total)} sur{' '}
                    <span className="font-bold text-ink">{pagination.total}</span> commandes
                  </div>

                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="h-8 px-2.5"
                    >
                      Précédent
                    </Button>
                    <span className="text-xs font-semibold px-2">
                      Page {page} / {pagination.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                      disabled={page >= pagination.totalPages}
                      className="h-8 px-2.5"
                    >
                      Suivant
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Panel>
      )}

      {/* MODAL 1: Tarifs Transporteurs */}
      {ratesModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto"
            onClick={(e) => {
              if (e.target === e.currentTarget) setRatesModalOpen(false);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-2xl bg-card border border-border rounded-xl shadow-2xl overflow-hidden my-auto animate-in fade-in zoom-in-95 duration-150"
            >
              <div className="p-4 border-b border-border bg-cream/30 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-ink">Grille Tarifaire Transporteurs (HT)</h3>
                  <p className="text-xs text-ink-ghost">
                    Exemple : 6.000 TND HT pour un colis livré et 0.000 TND pour un colis retourné.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setRatesModalOpen(false)}
                  className="text-ink-ghost hover:text-ink text-sm font-bold p-1"
                >
                  ✕
                </button>
              </div>

              <div className="p-4 max-h-[70vh] overflow-y-auto space-y-4">
                <TableShell>
                  <Table>
                    <THead>
                      <Tr>
                        <Th>Transporteur</Th>
                        <Th>Prix Livré HT (TND)</Th>
                        <Th>Prix Retour HT (TND)</Th>
                        <Th>Taux TVA</Th>
                      </Tr>
                    </THead>
                    <TBody>
                      {ratesForm.map((rate, idx) => (
                        <Tr key={rate.carrierKey}>
                          <Td className="font-bold text-ink text-xs">{rate.carrierLabel}</Td>
                          <Td>
                            <input
                              type="number"
                              step="0.05"
                              value={rate.deliveryPriceHt}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                setRatesForm((prev) =>
                                  prev.map((r, i) => (i === idx ? { ...r, deliveryPriceHt: val } : r)),
                                );
                              }}
                              className="w-24 h-8 px-2 text-xs font-mono font-bold bg-card border border-border rounded focus:ring-1 focus:ring-indigo-500"
                            />
                          </Td>
                          <Td>
                            <input
                              type="number"
                              step="0.05"
                              value={rate.returnPriceHt}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                setRatesForm((prev) =>
                                  prev.map((r, i) => (i === idx ? { ...r, returnPriceHt: val } : r)),
                                );
                              }}
                              className="w-24 h-8 px-2 text-xs font-mono font-bold bg-card border border-border rounded focus:ring-1 focus:ring-indigo-500"
                            />
                          </Td>
                          <Td className="text-xs text-ink-ghost">
                            <span className="font-mono">{(rate.vatRate * 100).toFixed(0)} %</span>
                          </Td>
                        </Tr>
                      ))}
                    </TBody>
                  </Table>
                </TableShell>
              </div>

              <div className="p-4 border-t border-border bg-cream/20 flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setRatesModalOpen(false)}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => saveRatesMutation.mutate(ratesForm)}
                  disabled={saveRatesMutation.isPending}
                  className="gap-1.5"
                >
                  {saveRatesMutation.isPending && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
                  Enregistrer la grille
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* MODAL 2: Synchroniser Statuts Woo */}
      {syncWooModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto"
            onClick={(e) => {
              if (e.target === e.currentTarget) setSyncWooModalOpen(false);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-2xl bg-card border border-border rounded-xl shadow-2xl overflow-hidden my-auto animate-in fade-in zoom-in-95 duration-150"
            >
              <div className="p-4 border-b border-border bg-emerald-50/50 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-emerald-950 flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 text-emerald-700" />
                    Mise à Jour des Statuts WooCommerce ({selectedYm})
                  </h3>
                  <p className="text-xs text-emerald-800">
                    Règle native Woo : Colis Livrés → « completed » (Terminée) & Colis Retours → « cancelled » (Annulée).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSyncWooModalOpen(false)}
                  className="text-ink-ghost hover:text-ink text-sm font-bold p-1"
                >
                  ✕
                </button>
              </div>

              <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Dry-run safety toggle */}
                <div className="p-3 bg-cream/40 rounded-lg border border-border flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold text-ink">Mode Simulation Sécurisée (Dry-Run)</div>
                    <div className="text-[11px] text-ink-ghost">
                      {syncWooDryRun
                        ? 'Aucune écriture sur Woo : prévisualise les commandes qui changeront de statut.'
                        : 'ATTENTION : Les statuts seront réellement modifiés sur la boutique WooCommerce en direct.'}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={syncWooDryRun}
                      onChange={(e) => setSyncWooDryRun(e.target.checked)}
                      className="h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-xs font-bold text-ink">Simulation</span>
                  </label>
                </div>

                {syncWooResults ? (
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg bg-emerald-100/50 border border-emerald-200 text-xs text-emerald-900 font-semibold flex items-center justify-between">
                      <div>
                        {syncWooResults.dryRun ? 'Simulation terminée' : 'Synchronisation réelle effectuée'} :{' '}
                        <span className="font-bold">{syncWooResults.totalUpdates}</span> commande(s) candidate(s).
                        {syncWooResults.dryRun ? (
                          <span className="ml-2 text-ink-ghost font-normal">
                            ({syncWooResults.countCompleted ?? 0} à passer en Terminée, {syncWooResults.countCancelled ?? 0} en Annulée)
                          </span>
                        ) : (
                          <span className="ml-2 font-normal">
                            (<span className="text-emerald-700 font-bold">{syncWooResults.successCount ?? 0} succès</span>,{' '}
                            <span className="text-rose-700 font-bold">{syncWooResults.failedCount ?? 0} échecs</span>)
                          </span>
                        )}
                      </div>
                    </div>

                    {syncWooResults.totalUpdates === 0 ? (
                      <div className="text-xs text-ink-faint p-4 text-center border border-dashed rounded-lg">
                        Aucune commande à mettre à jour. Tous les statuts sont déjà synchronisés !
                      </div>
                    ) : (
                      <TableShell>
                        <Table>
                          <THead>
                            <Tr>
                              <Th>Commande</Th>
                              <Th>Nouveau Statut</Th>
                              <Th className="text-center">Statut</Th>
                            </Tr>
                          </THead>
                          <TBody>
                            {syncWooResults.updates.map((res) => (
                              <Tr key={res.wooId}>
                                <Td className="font-mono text-xs font-bold">#{res.orderNumber}</Td>
                                <Td className="text-xs font-bold text-emerald-800">
                                  {res.targetStatus === 'completed' ? 'completed (Terminée)' : 'cancelled (Annulée)'}
                                </Td>
                                <Td className="text-center text-xs">
                                  {syncWooResults.dryRun ? (
                                    <span className="text-sky-700 font-bold">À mettre à jour</span>
                                  ) : res.success ? (
                                    <span className="text-emerald-700 font-bold">Mis à jour</span>
                                  ) : (
                                    <span className="text-rose-700 font-bold">{res.error ?? 'Erreur'}</span>
                                  )}
                                </Td>
                              </Tr>
                            ))}
                          </TBody>
                        </Table>
                      </TableShell>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-ink-soft space-y-2">
                    <p>
                      Cette action va scanner toutes les commandes du mois <strong>{selectedYm}</strong> ayant une preuve de livraison
                      conforme ou de retour, et dont le statut sur WooCommerce n'est pas encore synchronisé.
                    </p>
                    <p className="text-[11px] text-ink-ghost italic">
                      Conseil : Lancez d'abord avec « Simulation » cochée pour vérifier la liste avant d'appliquer sur le site réel.
                    </p>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-border bg-cream/20 flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setSyncWooModalOpen(false)}>
                  Fermer
                </Button>
                {syncWooResults?.dryRun && syncWooResults.totalUpdates > 0 ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setSyncWooDryRun(false);
                      syncWooStatusesMutation.mutate({
                        ym: selectedYm,
                        dryRun: false,
                      });
                    }}
                    disabled={syncWooStatusesMutation.isPending}
                    className="gap-1.5 bg-emerald-700 hover:bg-emerald-800"
                  >
                    {syncWooStatusesMutation.isPending && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
                    Appliquer réellement sur WooCommerce
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      syncWooStatusesMutation.mutate({
                        ym: selectedYm,
                        dryRun: syncWooDryRun,
                      })
                    }
                    disabled={syncWooStatusesMutation.isPending}
                    className="gap-1.5 bg-emerald-700 hover:bg-emerald-800"
                  >
                    {syncWooStatusesMutation.isPending && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
                    {syncWooDryRun ? 'Lancer la simulation' : 'Appliquer sur WooCommerce'}
                  </Button>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* MODAL 3: Liaison Manuelle */}
      {manualMatchModalOpen && selectedRecordForMatch &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto"
            onClick={(e) => {
              if (e.target === e.currentTarget) setManualMatchModalOpen(false);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-xl bg-card border border-border rounded-xl shadow-2xl overflow-hidden my-auto animate-in fade-in zoom-in-95 duration-150"
            >
              <div className="p-4 border-b border-border bg-indigo-50/50 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-indigo-950 flex items-center gap-2">
                    <LinkIcon className="h-4 w-4 text-indigo-700" />
                    Rapprochement Manuel de Colis
                  </h3>
                  <p className="text-xs text-indigo-800">
                    Lier le colis AWB {selectedRecordForMatch.trackingNumber} ({selectedRecordForMatch.carrierName}) à une commande Woo.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setManualMatchModalOpen(false)}
                  className="text-ink-ghost hover:text-ink text-sm font-bold p-1"
                >
                  ✕
                </button>
              </div>

              <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Record Summary Box */}
                <div className="p-3 bg-cream/40 rounded-lg border border-border text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-ink-ghost">Destinataire :</span>
                    <span className="font-bold text-ink">{selectedRecordForMatch.customerName || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink-ghost">Téléphone :</span>
                    <span className="font-mono font-bold text-indigo-950">{selectedRecordForMatch.customerPhone || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink-ghost">Montant COD transporteur :</span>
                    <span className="font-mono font-bold text-emerald-900">{formatTnd(selectedRecordForMatch.amount)}</span>
                  </div>
                </div>

                {/* Live search input */}
                <div>
                  <label className="block text-xs font-bold text-ink mb-1">
                    Rechercher la commande Woo correspondante :
                  </label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-ghost" />
                    <input
                      type="text"
                      value={manualMatchSearch}
                      onChange={(e) => setManualMatchSearch(e.target.value)}
                      placeholder="Tapez le numéro de commande, nom client ou tél…"
                      className="w-full h-8 pl-8 pr-3 text-xs bg-card border border-border rounded-lg focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Candidates list */}
                <div className="space-y-1.5">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-ink-ghost">
                    Commandes candidates de {selectedYm} :
                  </span>
                  {manualMatchCandidateOrders.length === 0 ? (
                    <div className="text-xs text-ink-faint p-4 text-center border border-dashed rounded-lg">
                      Aucune commande trouvée. Essayez une autre recherche.
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-52 overflow-y-auto">
                      {manualMatchCandidateOrders.map((cand) => (
                        <div
                          key={cand.order.id}
                          className="p-2 rounded-lg border border-border hover:border-indigo-400 bg-card hover:bg-indigo-50/20 flex items-center justify-between transition-colors"
                        >
                          <div className="text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-indigo-950">#{cand.order.number}</span>
                              <span className="font-semibold text-ink">{cand.order.customerName}</span>
                            </div>
                            <div className="text-[11px] text-ink-ghost font-mono">
                              {cand.order.billingPhone} · {formatTnd(cand.order.total)}
                            </div>
                          </div>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              manualMatchMutation.mutate({
                                carrierRecordId: selectedRecordForMatch.id,
                                wooOrderId: cand.order.wooId,
                              })
                            }
                            disabled={manualMatchMutation.isPending}
                            className="h-7 text-xs font-bold text-indigo-700 border-indigo-300 hover:bg-indigo-600 hover:text-white"
                          >
                            Associer
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 border-t border-border bg-cream/20 flex items-center justify-end">
                <Button variant="outline" size="sm" onClick={() => setManualMatchModalOpen(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* MODAL 4: Liaison Manuelle depuis Commande Non Rapprochée */}
      {orderMatchModalOpen && selectedOrderForMatch &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto"
            onClick={(e) => {
              if (e.target === e.currentTarget) setOrderMatchModalOpen(false);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-2xl bg-card border border-border rounded-xl shadow-2xl overflow-hidden my-auto animate-in fade-in zoom-in-95 duration-150"
            >
              <div className="p-4 border-b border-border bg-indigo-50/50 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-indigo-950 flex items-center gap-2">
                    <LinkIcon className="h-4 w-4 text-indigo-700" />
                    Rapprochement Manuel — Commande #{selectedOrderForMatch.order.number}
                  </h3>
                  <p className="text-xs text-indigo-800">
                    Associer cette commande WooCommerce à un colis orphelin reçu dans vos bordereaux transporteurs.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOrderMatchModalOpen(false)}
                  className="text-ink-ghost hover:text-ink text-sm font-bold p-1"
                >
                  ✕
                </button>
              </div>

              <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Commande Summary Box */}
                <div className="p-3 bg-cream/40 rounded-lg border border-border text-xs grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] text-ink-ghost uppercase tracking-wider font-semibold">Client & Contact</div>
                    <div className="font-bold text-ink text-sm">{selectedOrderForMatch.order.customerName || 'Client inconnu'}</div>
                    <div className="font-mono text-indigo-950 font-semibold">{selectedOrderForMatch.order.billingPhone || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-ink-ghost uppercase tracking-wider font-semibold">Montant & Lieu</div>
                    <div className="font-mono font-black text-emerald-900 text-sm">
                      {formatTnd(selectedOrderForMatch.order.total)}
                    </div>
                    <div className="text-ink-soft">
                      {[selectedOrderForMatch.order.billingCity, selectedOrderForMatch.order.billingGov]
                        .filter(Boolean)
                        .join(' — ') || '—'}
                    </div>
                  </div>
                </div>

                {/* Search input for carrier records */}
                <div>
                  <label className="block text-xs font-bold text-ink mb-1">
                    Rechercher un colis parmi les orphelins de {selectedYm} :
                  </label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-ghost" />
                    <input
                      type="text"
                      value={orderMatchSearch}
                      onChange={(e) => setOrderMatchSearch(e.target.value)}
                      placeholder="AWB, téléphone, nom client, référence…"
                      className="w-full h-8 pl-8 pr-3 text-xs bg-card border border-border rounded-lg focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Candidate orphan carrier parcels */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-ink-ghost">
                      Colis orphelins candidats ({candidateCarriersForOrder.length}) :
                    </span>
                    <span className="text-[10px] text-ink-faint">
                      {unmatchedCarriers.length} orphelin(s) au total en {selectedYm}
                    </span>
                  </div>

                  {candidateCarriersForOrder.length === 0 ? (
                    <div className="text-xs text-ink-faint p-6 text-center border border-dashed rounded-lg bg-cream/10">
                      Aucun colis orphelin correspondant trouvé pour cette recherche dans {selectedYm}.
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-60 overflow-y-auto">
                      {candidateCarriersForOrder.map((carrierRec) => {
                        const amountExactMatch =
                          selectedOrderForMatch.order.total !== null &&
                          Math.abs((selectedOrderForMatch.order.total ?? 0) - carrierRec.amount) < 0.01;

                        return (
                          <div
                            key={carrierRec.id}
                            className="p-2.5 rounded-lg border border-border hover:border-indigo-400 bg-card hover:bg-indigo-50/20 flex items-center justify-between gap-3 transition-colors"
                          >
                            <div className="text-xs flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-1.5 py-0.5 rounded bg-cream text-[10px] font-bold uppercase">
                                  {carrierRec.carrierName}
                                </span>
                                <span className="font-mono font-bold text-indigo-950">{carrierRec.trackingNumber}</span>
                                {carrierRec.orderRef && (
                                  <span className="text-[11px] text-ink-ghost font-mono">
                                    (Réf: {carrierRec.orderRef})
                                  </span>
                                )}
                                {amountExactMatch && (
                                  <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
                                    Montant identique
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-[11px] text-ink-soft">
                                <span className="font-medium text-ink truncate max-w-[140px]">
                                  {carrierRec.customerName || 'Client inconnu'}
                                </span>
                                <span className="font-mono text-ink-ghost">{carrierRec.customerPhone || '—'}</span>
                                <span className="text-ink-faint">{carrierRec.destination || '—'}</span>
                                <span className="text-ink-faint font-mono">
                                  {formatDateFr(carrierRec.deliveryDate)}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <span className="font-mono font-bold text-emerald-950 text-xs">
                                {formatTnd(carrierRec.amount)}
                              </span>

                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  manualMatchMutation.mutate({
                                    carrierRecordId: carrierRec.id,
                                    wooOrderId: selectedOrderForMatch.order.id,
                                  })
                                }
                                disabled={manualMatchMutation.isPending}
                                className="h-7 text-xs font-bold text-indigo-700 border-indigo-300 hover:bg-indigo-600 hover:text-white"
                              >
                                Associer
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 border-t border-border bg-cream/20 flex items-center justify-end">
                <Button variant="outline" size="sm" onClick={() => setOrderMatchModalOpen(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
